import { Link, useParams } from "react-router-dom";
import JsonLd from "../../components/JsonLd/JsonLd";
import Layout from "../../components/Layout/Layout";
import { siteById } from "../../data/sites";
import { bortleLabel, bortleTone, overnightLabel, TAG_LABELS } from "../../lib/access";
import { formatCoords } from "../../lib/geo";
import { SITE_ORIGIN } from "../../lib/site";

const SiteDetail = () => {
  const { id } = useParams();
  const site = id ? siteById(id) : undefined;

  return (
    <Layout title={site ? `${site.name} | Darkskies` : "Site | Darkskies"}>
      <section className="max-w-3xl mx-auto px-6 py-12 w-full">
        <Link to="/sites" className="text-turquoise hover:text-mars text-sm">
          ← Back to sites
        </Link>

        {!site && <p className="mt-8 text-lg">That site is not in the v1 list yet.</p>}

        {site && (
          <article className="mt-6">
            <JsonLd
              data={{
                "@context": "https://schema.org",
                "@type": "Place",
                name: site.name,
                url: `${SITE_ORIGIN}/sites/${site.id}`,
                description: site.whyGood,
                geo: {
                  "@type": "GeoCoordinates",
                  latitude: site.latitude,
                  longitude: site.longitude,
                },
                address: {
                  "@type": "PostalAddress",
                  addressRegion: site.state,
                  addressCountry: site.country ?? "US",
                },
              }}
            />
            <p className="text-sun font-semibold tracking-wide uppercase text-sm">
              {site.region} · {site.state}
            </p>
            <h1 className="text-4xl font-bold text-alt-blk mt-2">{site.name}</h1>
            <p className="text-xl text-alt-blk/70 mt-2">
              {bortleLabel(site.bortle)} · {bortleTone(site.bortle)}
            </p>
            <p className="text-sm text-alt-blk/50 mt-1">{site.bortleNote}</p>

            <dl className="grid sm:grid-cols-2 gap-4 mt-8 text-sm">
              <div className="rounded-2xl bg-white p-4 border border-alt-blk/10">
                <dt className="text-alt-blk/50">Overnight</dt>
                <dd className="mt-1 font-medium">{overnightLabel(site)}</dd>
              </div>
              <div className="rounded-2xl bg-white p-4 border border-alt-blk/10">
                <dt className="text-alt-blk/50">Coordinates</dt>
                <dd className="mt-1 font-medium">
                  {formatCoords(site.latitude, site.longitude)}
                </dd>
              </div>
            </dl>

            <div className="flex flex-wrap gap-1.5 mt-6">
              {site.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-[11px] uppercase tracking-wide px-2.5 py-1 rounded-full border border-alt-blk/15"
                >
                  {TAG_LABELS[tag]}
                </span>
              ))}
            </div>

            <h2 className="text-2xl font-bold mt-10 mb-3">Why it is on the list</h2>
            <p className="leading-relaxed">{site.whyGood}</p>

            <h2 className="text-2xl font-bold mt-10 mb-3">Access</h2>
            <p className="leading-relaxed">{site.accessNotes}</p>

            <h2 className="text-2xl font-bold mt-10 mb-3">Length and rig notes</h2>
            <p className="leading-relaxed">{site.lengthNotes}</p>

            <h2 className="text-2xl font-bold mt-10 mb-3">Etiquette</h2>
            <p className="leading-relaxed">{site.etiquette}</p>

            <div className="flex flex-wrap gap-3 mt-10">
              <Link
                to={`/?site=${site.id}`}
                className="inline-block bg-mars hover:bg-sunset text-white px-8 py-4 rounded-2xl font-semibold"
              >
                Show on map
              </Link>
              {site.officialUrl && (
                <a
                  href={site.officialUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block border border-alt-blk px-8 py-4 rounded-2xl font-semibold hover:bg-alt-blk hover:text-white transition-colors"
                >
                  Official page
                </a>
              )}
            </div>
          </article>
        )}
      </section>
    </Layout>
  );
};

export default SiteDetail;
